import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { navBridge } from '../services/nav-bridge';
// Liefert `totalActiveEmployees` synchron an die `skipIf`-Closures. Der
// Coordinator laedt den Snapshot vor dem Start dieser Tour vor (siehe
// tour-coordinator.service.ts, Preload-Block in `startTour`).
import { planningReadinessBridge } from '../services/planning-readiness-bridge';

const goToTimeRecording = async () => {
  await navBridge.go('/time-recording');
};

/**
 * Setup-Tour „Mitarbeiterzeiterfassung" — 7. Schritt im Setup-Funnel,
 * direkt nach `staffing-management`. Goldpfad: User klickt eine leere
 * Tageszelle eines Mitarbeiters → Dialog oeffnet sich → User fuegt einen
 * echten Beispiel-Zeiteintrag mit Start/Ende ein → speichert → sieht
 * ARBEITSZEIT/PAUSENZEIT-Aggregate aktualisiert. Abschluss: Erklaerung der
 * drei Auswertungs-Buttons (Live-Bericht, Analyse, Bericht) oben rechts.
 *
 * 11 Steps in 3 Kapiteln (`tr-overview`, `tr-edit-day`, `tr-insights`).
 *
 * Tour folgt dem User: aktive Klick-Steps (Tageszelle, Add-Button-Klick im
 * Dialog-Header-Step, Save) advancen via CustomEvents, die die Time-Recording-
 * Components beim echten User-Klick dispatchen. Steps innerhalb des Dialogs
 * haben `attachToDialog` + `popoverOutsideDialog` und einen gemeinsamen
 * `resumeRecoveryStepId` `tr-day-cell` — bei Pause/Resume ohne offenen Dialog
 * reproduziert der User die Sequenz ab dem Klick auf eine Tageszelle.
 *
 * Hinweis: Der ehemalige Step `tr-add-button` wurde in `tr-dialog-header`
 * konsolidiert. Begruendung: Wenn der User direkt auf "Zeiteintrag hinzufuegen"
 * klickt (statt auf "Weiter"), verschwindet der Add-Button via
 * `*ngIf="!newEvent"` aus dem DOM, der separate Step war dann nicht mehr
 * renderbar und wurde via skipIf/forward-skip uebersprungen — visuell wirkte
 * das wie ein fehlender Step. Der konsolidierte Step erklaert Dialog-Aufbau
 * und Add-Button gemeinsam, mit additional spotlight auf dem Add-Button und
 * Advance per `tour:time-entry-add-form-opened` (Klick) oder Weiter-Button.
 */
export const TIME_RECORDING_ONBOARDING_TOUR: TourConfig = {
  id: TourFeature.TimeRecording,
  version: 1,
  title: 'CHECKLIST.TIME_RECORDING.TITLE',
  description: 'CHECKLIST.TIME_RECORDING.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Tour zeigt Zeit-Korrekturen und Terminal-Verwaltung. Der User braucht
  // entweder Edit-Recht fuer Team/All-Zeiten ODER Terminal-Management;
  // sonst ist die Tour reine Theorie.
  requiredPermissions: [
    [
      'TimeTracking.CanEditTeamTime',
      'TimeTracking.CanEditAllTime',
      'TimeTracking.CanManageTerminal',
    ],
  ],
  triggers: [TourTrigger.Manual, TourTrigger.Resume],
  triggerContext: {
    route: '/time-recording',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 72,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  priority: 60,
  estimatedDurationSec: 360,
  category: 'setup',
  chapters: [
    {
      id: 'tr-overview',
      title: 'CHECKLIST.TIME_RECORDING.CHAPTER_OVERVIEW',
      steps: [
        {
          // Welcome-Step. Anchor: erste Mitarbeiter-Zeile (klein, links).
          // placement 'right' setzt den Popover rechts daneben uebers
          // Calendar-Grid — laesst die Mitarbeiter-Spalte links komplett
          // sichtbar. Spotlight rahmt den ARBEITS-Bereich (Resource-Liste
          // + Calendar-Grid), aber NICHT den Page-Titel oben (Wrapper-
          // Anchor `time-recording-page` bewusst nicht verwendet).
          id: 'tr-welcome',
          anchor: AnchorId.TimeRecordingFirstEmployeeRow,
          spotlightAnchor: AnchorId.TimeRecordingResourceList,
          additionalSpotlightAnchors: [AnchorId.TimeRecordingCalendarGrid],
          title: 'TUTORIAL.TIME_RECORDING.V1.WELCOME.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.WELCOME.CONTENT',
          placement: 'right',
          showNextButton: true,
          onBeforeShow: goToTimeRecording,
          // Fachliche Absicht unveraendert: Ohne Mitarbeiter ist dieser Step
          // sinnlos und soll uebersprungen werden. Geprueft wird das aber an der
          // DATENLAGE statt am DOM (umgestellt 25.08.2026, im Browser gemessen).
          //
          // Vorher stand hier eine Anchor-Abfrage auf
          // `[onboardingAnchor="time-recording-first-employee-row"]`. Die konnte
          // die Frage „gibt es Mitarbeiter?" nicht beantworten, weil sie zum
          // falschen Zeitpunkt lief: `skipIf` wird VOR `onBeforeShow` und vor dem
          // Anchor-Warten ausgewertet (tour-coordinator.service.ts:958 vs. :1014
          // vs. :1048). Die Tour navigiert per `triggerContext.route` nach
          // `/time-recording` und wartet dort nur 300 ms (:3315) — die Zeilen
          // haengen aber an einem Backend-Call und erscheinen erst ~900 ms
          // spaeter. Gemessen beim Start vom Dashboard:
          //   0,2 s Route gewechselt · 0,2 s Anchor fehlt · 1,1 s Anchor da.
          // Ergebnis: Dieser Step UND `tr-employee-row` fielen in derselben
          // Sekunde weg — bei einem Mandanten mit 29 Mitarbeitern. Der Nutzer sah
          // als Erstes „Woche und Ansicht waehlen" (Step 3 von 11).
          //
          // Der Readiness-Snapshot kennt die Zahl sofort und ohne Render-Rennen.
          // `null` (noch nicht geladen) heisst bewusst NICHT ueberspringen: dann
          // entscheidet das Anchor-Warten (MutationObserver, 8 s), das die Zeile
          // im Test bei 1,1 s zuverlaessig fand.
          skipIf: () => {
            const s = planningReadinessBridge.snapshot();
            return !!s && s.totalActiveEmployees === 0;
          },
        },
        {
          id: 'tr-employee-row',
          anchor: AnchorId.TimeRecordingFirstEmployeeRow,
          spotlightAnchor: AnchorId.TimeRecordingFirstEmployeeRow,
          additionalSpotlightAnchors: [AnchorId.TimeRecordingResourceList],
          title: 'TUTORIAL.TIME_RECORDING.V1.EMPLOYEE_ROW.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.EMPLOYEE_ROW.CONTENT',
          placement: 'right',
          showNextButton: true,
          onBeforeShow: goToTimeRecording,
          // Gleiche Umstellung wie bei `tr-welcome`: Datenlage statt Anchor.
          // Beide Steps pruefen dieselbe Voraussetzung und fielen vorher
          // gemeinsam weg, weil sie am selben, noch nicht gerenderten Anchor
          // hingen.
          skipIf: () => {
            const s = planningReadinessBridge.snapshot();
            return !!s && s.totalActiveEmployees === 0;
          },
        },
        {
          id: 'tr-week-nav',
          anchor: AnchorId.TimeRecordingNavigation,
          spotlightAnchor: AnchorId.TimeRecordingNavigation,
          additionalSpotlightAnchors: [AnchorId.TimeRecordingViewButtons],
          title: 'TUTORIAL.TIME_RECORDING.V1.WEEK_NAV.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.WEEK_NAV.CONTENT',
          placement: 'bottom',
          showNextButton: true,
          onBeforeShow: goToTimeRecording,
        },
      ],
    },
    {
      id: 'tr-edit-day',
      title: 'CHECKLIST.TIME_RECORDING.CHAPTER_EDIT_DAY',
      steps: [
        {
          // Aktiver Klick-Step: User klickt eine LEERE Zelle in einer
          // Mitarbeiter-Zeile. CustomEvent feuert in
          // `admin-time-recording-timeline.component.ts:addEditTimeRecordingEvent`
          // direkt nach `dialog.open(...)` — robust gegen FullCalendar-
          // Re-Mount im Gegensatz zu naivem `nextCondition: 'onClick'`.
          //
          // Spotlight: die KLICKBARE Lane des ersten Mitarbeiters RECHTS
          // (nicht die Namens-Box links!). Cutout ist eine schmale
          // horizontale Reihe (~ 1148 × 70 px) — leitet das Auge zu den
          // tatsaechlich klickbaren Zellen. Anker ist trotzdem die Cell-
          // Area, weil das Popover ueber dem Calendar-Grid sein soll.
          id: 'tr-day-cell',
          anchor: AnchorId.TimeRecordingCellArea,
          spotlightAnchor: AnchorId.TimeRecordingFirstEmployeeLane,
          title: 'TUTORIAL.TIME_RECORDING.V1.DAY_CELL.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.DAY_CELL.CONTENT',
          placement: 'bottom',
          advanceOnDocumentEvent: 'tour:time-recording-dialog-opened',
          showNextButton: false,
          onBeforeShow: goToTimeRecording,
        },
        {
          // Konsolidierter Step (frueher zwei: tr-dialog-header + tr-add-button).
          // Spotlight rahmt den Dialog als ganze Box plus zusaetzlich den
          // Add-Button — so erkennt der User auf einen Blick, dass das
          // Klicken des Buttons der naechste Schritt ist.
          //
          // Advance: primaer via CustomEvent `tour:time-entry-add-form-opened`
          // (Klick auf Add-Button feuert sofort + dispatcht nach DOM-Update).
          // Sekundaer: `requiredAnchors: [TimeEntryNewCard]` macht den Weiter-
          // Button enabled, sobald die Inline-Card im DOM ist (also nach Klick
          // auf Add-Button). So bleibt der User nicht haengen, falls das
          // CustomEvent eine Race-Condition trifft (z. B. nach Resume mit
          // bereits offener Card). Wenn die Card noch nicht da ist, blockiert
          // requiredAnchors NICHT (Coordinator-Konvention), aber dann fuehrt
          // der natuerliche Klick auf den Add-Button die Tour weiter.
          id: 'tr-dialog-header',
          anchor: AnchorId.TimeRecordingDialog,
          spotlightAnchor: AnchorId.TimeRecordingDialog,
          additionalSpotlightAnchors: [AnchorId.TimeEntryAddButton],
          title: 'TUTORIAL.TIME_RECORDING.V1.DIALOG_HEADER.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.DIALOG_HEADER.CONTENT',
          placement: 'left',
          advanceOnDocumentEvent: 'tour:time-entry-add-form-opened',
          showNextButton: true,
          showPrevButton: false,
          requiredAnchors: [AnchorId.TimeEntryNewCard],
          dialogContext: {
            attachToDialog: true,
            waitForOpen: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTimeRecording,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="time-recording-dialog"]'),
          resumeRecoveryStepId: 'tr-day-cell',
        },
        {
          id: 'tr-type-select',
          anchor: AnchorId.TimeEntryTypeSelect,
          spotlightAnchor: AnchorId.TimeEntryNewCard,
          additionalSpotlightAnchors: [AnchorId.TimeEntryTypeSelect],
          title: 'TUTORIAL.TIME_RECORDING.V1.TYPE_SELECT.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.TYPE_SELECT.CONTENT',
          placement: 'right',
          showNextButton: true,
          showPrevButton: false,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTimeRecording,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="time-entry-new-card"]'),
          resumeRecoveryStepId: 'tr-day-cell',
        },
        {
          // Bewusst manueller Next-Button (NICHT `nextCondition: 'onChange'`):
          // Bei Pause/Resume hat der Input bereits einen Wert; mit onChange
          // muesste der User leeren und neu tippen. Mit Next-Button kommt der
          // Resume-Pfad ohne Bridge-State aus.
          //
          // `requiredAnchors: [self]` graut den Weiter-Button aus, solange das
          // Start-Feld leer ist — gibt visuelles Feedback, dass eine Eingabe
          // erwartet wird, ohne den Resume-Pfad zu brechen (Resume mit Wert =
          // Button sofort enabled).
          id: 'tr-start-input',
          anchor: AnchorId.TimeEntryStartInput,
          spotlightAnchor: AnchorId.TimeEntryNewCard,
          additionalSpotlightAnchors: [AnchorId.TimeEntryStartInput],
          title: 'TUTORIAL.TIME_RECORDING.V1.START_INPUT.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.START_INPUT.CONTENT',
          placement: 'right',
          showNextButton: true,
          requiredAnchors: [AnchorId.TimeEntryStartInput],
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTimeRecording,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="time-entry-start-input"]'),
          resumeRecoveryStepId: 'tr-day-cell',
        },
        {
          // Analog zu `tr-start-input`: requiredAnchors graut Weiter aus,
          // bis das End-Feld einen Wert hat. Resume-freundlich.
          id: 'tr-end-input',
          anchor: AnchorId.TimeEntryEndInput,
          spotlightAnchor: AnchorId.TimeEntryNewCard,
          additionalSpotlightAnchors: [AnchorId.TimeEntryEndInput],
          title: 'TUTORIAL.TIME_RECORDING.V1.END_INPUT.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.END_INPUT.CONTENT',
          placement: 'right',
          showNextButton: true,
          requiredAnchors: [AnchorId.TimeEntryEndInput],
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTimeRecording,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="time-entry-end-input"]'),
          resumeRecoveryStepId: 'tr-day-cell',
        },
        {
          // Save-Step. CustomEvent in
          // `show-time-recording-details.component.ts:saveNewEvent` feuert NUR
          // nach erfolgreichem Backend-Call (im try-Block, vor catch). Bei
          // Validierungs-Fehlschlag bleibt die Tour auf diesem Step, der User
          // korrigiert und drueckt erneut Save.
          id: 'tr-save',
          anchor: AnchorId.TimeEntrySaveButton,
          spotlightAnchor: AnchorId.TimeEntryNewCard,
          additionalSpotlightAnchors: [AnchorId.TimeEntrySaveButton],
          title: 'TUTORIAL.TIME_RECORDING.V1.SAVE.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.SAVE.CONTENT',
          placement: 'top',
          align: 'end',
          advanceOnDocumentEvent: 'tour:time-entry-saved',
          showNextButton: false,
          dialogContext: {
            attachToDialog: true,
            popoverOutsideDialog: true,
          },
          onBeforeShow: goToTimeRecording,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="time-entry-save-button"]'),
          resumeRecoveryStepId: 'tr-day-cell',
        },
      ],
    },
    {
      id: 'tr-insights',
      title: 'CHECKLIST.TIME_RECORDING.CHAPTER_INSIGHTS',
      steps: [
        {
          // Recap-Step: Aggregate sind jetzt sichtbar (Save hat sie befuellt).
          // `advanceOnDialogClose: true` analog zum Erfolgs-Step der staffing-
          // management-Tour: Schliessen des Dialogs ist hier der natuerliche
          // Uebergang zum Outro (das ausserhalb des Dialogs liegt).
          //
          // Spotlight: alle drei wichtigen Bereiche (ARBEITSZEIT-Card,
          // PAUSENZEIT-Card, neuer Time-Entry). Anker = TimeEntryCard
          // (unten Mitte im Dialog), placement 'right' — Popover landet
          // rechts NEBEN dem Eintrag im Dialog-Rechtsbereich. Damit
          // bleiben die ARBEITSZEIT/PAUSENZEIT-Cards oben komplett
          // sichtbar (sie liegen ueber dem Anker, der popover greift
          // nur unten rechts in den Eintraege-Bereich, NICHT auf die
          // Stats-Cards).
          id: 'tr-stats-recap',
          anchor: AnchorId.TimeEntryCard,
          spotlightAnchor: AnchorId.TimeRecordingWorkSummary,
          additionalSpotlightAnchors: [
            AnchorId.TimeRecordingBreakSummary,
            AnchorId.TimeEntryCard,
          ],
          title: 'TUTORIAL.TIME_RECORDING.V1.STATS_RECAP.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.STATS_RECAP.CONTENT',
          placement: 'right',
          showNextButton: true,
          showPrevButton: false,
          dialogContext: {
            attachToDialog: true,
            advanceOnDialogClose: true,
          },
          onBeforeShow: goToTimeRecording,
          skipIf: () =>
            typeof document !== 'undefined' &&
            !document.querySelector('[onboardingAnchor="time-recording-work-summary"]'),
          resumeRecoveryStepId: 'tr-day-cell',
        },
        {
          // Outro: Drei Auswertungs-Buttons. Der Coordinator schliesst den
          // Dialog beim Uebergang vom dialog-bound `tr-stats-recap` auf
          // diesen non-dialog Step automatisch (runTourV2 'next'-Branch +
          // 300ms Close-Animation).
          id: 'tr-outro',
          anchor: AnchorId.TimeRecordingLiveReport,
          spotlightAnchor: AnchorId.TimeRecordingLiveReport,
          additionalSpotlightAnchors: [
            AnchorId.TimeRecordingAnalysis,
            AnchorId.TimeRecordingReport,
          ],
          title: 'TUTORIAL.TIME_RECORDING.V1.OUTRO.TITLE',
          content: 'TUTORIAL.TIME_RECORDING.V1.OUTRO.CONTENT',
          placement: 'bottom',
          align: 'end',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: goToTimeRecording,
        },
      ],
    },
  ],
};
